"use client";

import { useEffect, useState } from "react";
import { AuthButton } from "@/components/auth-button";
import { useAuth } from "@/components/auth-provider";
import { PriceText } from "@/components/price-text";
import { Skeleton } from "@/components/skeleton";
import { useOfferCountdown } from "@/hooks/use-offer-countdown";
import { useSectionViewEvent } from "@/hooks/use-section-view-event";
import { useStartingPrice } from "@/hooks/use-starting-price";
import { fetchPricingPlans, type PricingPlan } from "@/lib/api/pricing-plans";
import { piEvents } from "@/lib/analytics";
import { PI_LOGIN_ENTRY_POINT, PI_SECTION } from "@/lib/analytics/schema";
import { DEFAULT_DISCOUNT_PERCENT, PRICING_COPY } from "@/lib/site-copy";

const CHECK_ICON = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

/** Duration-based label used by the purchase event, falling back to the plan name. */
function toPlanDurationLabel(plan: PricingPlan, position: number) {
  const months = plan.durationDays ? Math.round(plan.durationDays / 30) : 0;

  if (months === 1) return "monthly";
  if (months === 3) return "quarterly";
  if (months === 6) return "half_yearly";
  if (months === 12) return "yearly";
  if (months > 1) return `${months}_months`;

  return (
    (plan.name || plan.id)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "") || `plan_${position}`
  );
}

export function PricingSection() {
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { isSignedIn } = useAuth();
  const startingPrice = useStartingPrice();
  const countdown = useOfferCountdown();
  const discountPercent = startingPrice?.discountPercent ?? DEFAULT_DISCOUNT_PERCENT;

  useSectionViewEvent({
    sectionId: "pricing",
    sectionName: PI_SECTION.PRICING.name,
    sectionPosition: PI_SECTION.PRICING.position,
    enabled: Boolean(plans.length),
  });

  useEffect(() => {
    fetchPricingPlans().then((result) => {
      setPlans(result);
      setIsLoading(false);
    });
  }, []);

  if (!isLoading && !plans.length) {
    return null;
  }

  return (
    <section id="pricing" className="scroll-mt-20 bg-pi-bg">
      <div className="mx-auto max-w-[1200px] px-4 py-14 sm:px-6 md:py-20 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-[620px] text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[rgba(253,189,56,0.45)] bg-[rgba(253,189,56,0.12)] px-3.5 py-1.5 text-[13px] font-bold text-pi-gold">
            <span className="size-1.5 shrink-0 animate-pulse rounded-full bg-[#FF5A3C]" />
            {PRICING_COPY.offerEndsPrefix}{" "}
            <PriceText
              value={countdown.clock}
              className="min-w-[6ch] tabular-nums"
            />
          </div>
          <h2 className="m-0 text-[clamp(26px,4vw,34px)] font-bold leading-tight text-pi-bright">
            {PRICING_COPY.headingPrefix} {discountPercent}%{" "}
            {PRICING_COPY.headingSuffix}
          </h2>
          <p className="mt-3.5 mb-0 text-[clamp(15px,1.5vw,18px)] leading-relaxed text-pi-text">
            {PRICING_COPY.subheading}
          </p>
        </div>

        <div className="mt-11 grid grid-cols-1 items-stretch gap-5 md:grid-cols-3">
          {isLoading
            ? Array.from({ length: 3 }, (_, index) => (
                <PlanCardSkeleton key={index} />
              ))
            : plans.map((plan, index) =>
                plan.highlighted ? (
                  <div
                    key={plan.id}
                    className="rounded-[20px] bg-gradient-to-b from-pi-gold-start to-pi-gold p-0.5"
                  >
                    <PlanCard
                      plan={plan}
                      position={index + 1}
                      isSignedIn={isSignedIn}
                      className="relative rounded-[18px] bg-pi-elevated p-7"
                    />
                  </div>
                ) : (
                  <PlanCard
                    key={plan.id}
                    plan={plan}
                    position={index + 1}
                    isSignedIn={isSignedIn}
                    className="rounded-[18px] border border-pi-border-muted bg-pi-elevated p-7"
                  />
                ),
              )}
        </div>

        <p className="mt-7 mb-0 text-center text-sm text-pi-dim">
          ✓ Cancel anytime&nbsp;&nbsp;·&nbsp;&nbsp;✓ No hidden
          charges&nbsp;&nbsp;·&nbsp;&nbsp;✓ Renew or stop whenever you want
        </p>
      </div>
    </section>
  );
}

function PlanCardSkeleton() {
  return (
    <div className="flex h-full flex-col rounded-[18px] border border-pi-border-muted bg-pi-elevated p-7">
      <Skeleton className="h-6 w-32" />
      <Skeleton className="mt-2 h-4 w-40" />
      <Skeleton className="mt-5 h-5 w-24" />
      <Skeleton className="mt-2 h-10 w-36" />
      <div className="h-6" />
      <div className="mb-6 flex flex-col gap-3">
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-full" />
        <Skeleton className="h-5 w-full" />
      </div>
      <Skeleton className="mt-auto h-[52px] w-full rounded-xl" />
    </div>
  );
}

function PlanCard({
  plan,
  position,
  isSignedIn,
  className,
}: {
  plan: PricingPlan;
  position: number;
  isSignedIn: boolean;
  className: string;
}) {
  const planDurationLabel = toPlanDurationLabel(plan, position);
  return (
    <div className={`flex h-full min-w-0 flex-col break-words ${className}`}>
      {plan.badge ? (
        <div className="absolute top-0 right-6 max-w-[calc(100%-3rem)] -translate-y-1/2 rounded-full bg-gradient-to-b from-pi-gold-start to-pi-gold px-3 py-1 text-[11px] font-semibold tracking-wider text-pi-bg">
          {plan.badge.toUpperCase()}
        </div>
      ) : null}
      <div className="text-lg font-semibold text-pi-bright">{plan.name}</div>
      <div className="mt-1 text-sm text-pi-dim">{plan.billingCycle}</div>
      {plan.originalPrice ? (
        <div className="mt-5 text-base text-pi-dim line-through">
          {plan.originalPrice}
        </div>
      ) : null}
      <div
        className={`flex flex-wrap items-baseline gap-1.5 ${plan.originalPrice ? "" : "mt-5"}`}
      >
        <span className="text-4xl font-semibold text-pi-bright">
          {plan.price}
        </span>
        <span className="text-[15px] text-pi-text">{plan.priceSuffix}</span>
      </div>
      {plan.savingsNote ? (
        <div className="mt-2 text-sm text-pi-accent-soft">
          {plan.savingsNote}
        </div>
      ) : (
        <div className="h-6" />
      )}
      <ul
        className={`m-0 flex list-none flex-col gap-3 p-0 ${plan.highlighted ? "mt-5" : "mt-0"} mb-6`}
      >
        {plan.features.map((feature) => (
          <li
            key={feature}
            className={`flex min-w-0 items-center gap-2.5 text-sm break-words ${
              plan.highlighted ? "text-pi-bright" : "text-pi-text"
            }`}
          >
            {plan.highlighted ? (
              <span aria-hidden="true">✨</span>
            ) : (
              <span className="flex shrink-0 text-pi-success">
                {CHECK_ICON}
              </span>
            )}
            {feature}
          </li>
        ))}
      </ul>
      <AuthButton
        href={plan.checkoutUrl}
        variant={plan.highlighted ? "gold" : "outline"}
        loginEntryPoint={PI_LOGIN_ENTRY_POINT.PRICING_PLAN_CARD}
        selectedPlan={planDurationLabel}
        trackClick={() =>
          piEvents.purchaseButtonClicked({
            planSelected: {
              plan_id: plan.planId,
              tier_id: plan.id,
              amount: plan.amount,
              duration: plan.durationDays,
            },
            buttonVariant: `choose_${planDurationLabel}`,
            planPosition: position,
            isRecommended: plan.highlighted,
            postClickDestination: isSignedIn ? "checkout" : "login",
          })
        }
        className="mt-auto break-words"
      >
        {plan.ctaLabel}
      </AuthButton>
    </div>
  );
}
